import { ArrowRight, Phone } from "lucide-react";
import { ResponsiveImage } from "@/components/ResponsiveImage";
import { SITE } from "@/lib/site";

type ImageProps = {
  src: string;
  width: number;
  height: number;
  sources: { type: string; srcSet: string }[];
  sizes: string;
  alt: string;
};

export type PageHeroCta = {
  label: string;
  href: string;
  external?: boolean;
};

type Props = {
  eyebrow?: string;
  title: React.ReactNode;
  intro: string;
  primaryCta?: PageHeroCta;
  secondaryCta?: PageHeroCta;
  /** Show the phone CTA (defaults true). */
  showPhoneCta?: boolean;
  /** Optional background image — heroes above the fold can set priority. */
  backgroundImage?: ImageProps;
  /** Prefer high fetch priority when this hero is the LCP element. */
  priority?: boolean;
};

export function PageHero({
  eyebrow,
  title,
  intro,
  primaryCta,
  secondaryCta,
  showPhoneCta = true,
  backgroundImage,
  priority = false,
}: Props) {
  return (
    <section className="relative isolate overflow-hidden bg-secondary text-secondary-foreground">
      {backgroundImage && (
        <ResponsiveImage
          {...backgroundImage}
          alt={backgroundImage.alt}
          pictureClassName="absolute inset-0 h-full w-full"
          className="h-full w-full object-cover opacity-40"
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : "low"}
        />
      )}
      <div
        className={
          backgroundImage
            ? "absolute inset-0 bg-gradient-to-r from-secondary via-secondary/80 to-transparent"
            : "absolute inset-0 bg-secondary"
        }
      />
      <div className="relative mx-auto max-w-7xl px-4 py-16 md:py-20 lg:px-8 lg:py-24">
        {eyebrow && (
          <div className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-secondary/50 px-3 py-1 font-display text-xs tracking-[0.3em] text-accent">
            <span className="h-2 w-2 rounded-full bg-accent" /> {eyebrow}
          </div>
        )}
        <h1 className="mt-6 max-w-4xl font-display text-4xl leading-[0.95] text-secondary-foreground sm:text-5xl lg:text-6xl xl:text-7xl">
          {title}
        </h1>
        <p className="mt-5 max-w-2xl text-lg leading-7 text-secondary-foreground/80">{intro}</p>
        {(primaryCta || secondaryCta || showPhoneCta) && (
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            {primaryCta && (
              <a
                href={primaryCta.href}
                target={primaryCta.external ? "_blank" : undefined}
                rel={primaryCta.external ? "noopener noreferrer" : undefined}
                className="group inline-flex h-14 shrink-0 items-center justify-center gap-2 rounded-md bg-primary px-6 font-display tracking-wider text-primary-foreground hover:bg-primary/90"
              >
                {primaryCta.label}
                <ArrowRight
                  className="h-4 w-4 transition-transform group-hover:translate-x-1"
                  aria-hidden="true"
                />
              </a>
            )}
            {secondaryCta && (
              <a
                href={secondaryCta.href}
                target={secondaryCta.external ? "_blank" : undefined}
                rel={secondaryCta.external ? "noopener noreferrer" : undefined}
                className="inline-flex h-14 shrink-0 items-center justify-center gap-2 rounded-md bg-accent px-6 font-display tracking-wider text-accent-foreground hover:bg-accent/90"
              >
                {secondaryCta.label}
              </a>
            )}
            {showPhoneCta && (
              <a
                href={`tel:${SITE.phoneTel}`}
                className="inline-flex h-14 shrink-0 items-center justify-center gap-2 rounded-md border border-secondary-foreground/30 px-6 font-display tracking-wider text-secondary-foreground hover:border-accent hover:text-accent"
              >
                <Phone className="h-4 w-4" aria-hidden="true" /> {SITE.phoneDisplay}
              </a>
            )}
          </div>
        )}
      </div>
      <div className="diag-stripes absolute bottom-0 left-0 right-0 h-3" />
    </section>
  );
}
